import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Drama, MapPin, BadgeCheck, Quote, Calendar, Clock, ArrowRight,
  Users, Share2, Loader2, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#F97316";

/**
 * Public profile for L17 Theater Group at /theater-group/:handle.
 * No auth required — shareable to critics, funders, journalists.
 */
export default function PublicTheaterGroup() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [styleCatalog, setStyleCatalog] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/theater-groups/${handle}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Group not found."));
    axios.get(`${API}/meta/theater-styles`)
      .then(({ data }) => setStyleCatalog(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [handle]);

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/theater-group/${handle}`
    : "";

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: data?.group?.name || "Theater Group",
          text: `Check out ${data?.group?.name} on Call·Sheet`,
          url: shareUrl,
        });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard.");
    } catch { /* user dismissed */ }
  };

  if (err) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-20 text-center"
           data-testid="public-theater-group-error">
        <Drama size={40} className="mx-auto text-white/25 mb-4"/>
        <div className="text-white/70 text-lg">{err}</div>
        <Link to="/directory/orgs?type=theater_group"
              className="mt-6 inline-block text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-white underline underline-offset-4">
          Browse other groups
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-20 text-center text-white/50"
           data-testid="public-theater-group-loading">
        <Loader2 size={16} className="animate-spin inline mr-2"/> loading…
      </div>
    );
  }

  const { group, upcoming, past } = data;
  const styleLabel = (k) => styleCatalog.find((s) => s.key === k)?.label || k;

  return (
    <div className="min-h-screen" data-testid="public-theater-group">
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-white/8">
        {group.hero_image_url && (
          <img src={group.hero_image_url} alt=""
               className="absolute inset-0 w-full h-full object-cover opacity-25"/>
        )}
        <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse at top, ${ACCENT}22 0%, transparent 60%)`,
        }}/>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            L17 · Theater Group
          </div>
          <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tight"
                style={{ color: ACCENT }}
                data-testid="public-tg-name">
              {group.name}
            </h1>
            <Button onClick={share}
                    className="h-10 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white"
                    data-testid="public-tg-share">
              <Share2 size={12} className="mr-2"/> Share
            </Button>
          </div>
          <div className="mt-3 text-sm text-white/60 flex items-center gap-3 flex-wrap">
            {group.legal_name && group.legal_name !== group.name && <span>{group.legal_name}</span>}
            {group.verified && (
              <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300">
                <BadgeCheck size={10} className="inline mr-1"/> Verified
              </span>
            )}
            {group.city && (
              <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
                <MapPin size={10} className="inline mr-1"/> {group.city}
                {group.state && `, ${group.state}`}
              </span>
            )}
          </div>

          {(group.theater_styles || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="public-tg-styles">
              {group.theater_styles.map((s) => (
                <span key={s}
                      className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase"
                      style={{ background: `${ACCENT}22`, color: ACCENT }}>
                  {styleLabel(s)}
                </span>
              ))}
            </div>
          )}

          {group.bio && (
            <p className="mt-6 max-w-3xl text-[15px] text-white/75 leading-relaxed"
               data-testid="public-tg-bio">
              {group.bio}
            </p>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 space-y-14">
        {/* UPCOMING */}
        <Section title="Upcoming Shows" testId="public-tg-upcoming"
                 empty={upcoming.length === 0 && "No upcoming productions announced yet."}>
          {upcoming.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcoming.map((p) => (
                <ProductionCard key={p.id} p={p} styleLabel={styleLabel} accent={ACCENT} upcoming/>
              ))}
            </div>
          )}
        </Section>

        {/* PAST */}
        {past.length > 0 && (
          <Section title="Past Repertoire" testId="public-tg-past">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {past.map((p) => (
                <ProductionCard key={p.id} p={p} styleLabel={styleLabel} accent={ACCENT}/>
              ))}
            </div>
          </Section>
        )}

        {/* PRESS */}
        {(group.press_quotes || []).length > 0 && (
          <Section title="Press" testId="public-tg-press">
            <div className="grid sm:grid-cols-2 gap-4">
              {group.press_quotes.map((q, i) => (
                <blockquote key={i}
                            className="cs-card rounded-xl p-6 relative"
                            data-testid={`public-tg-press-${i}`}>
                  <Quote size={16} className="absolute top-4 left-4" style={{ color: ACCENT }}/>
                  <p className="pl-8 text-[15px] text-white/85 leading-relaxed italic">
                    "{q.quote}"
                  </p>
                  <footer className="mt-4 pl-8 text-[11px] tracking-[0.18em] uppercase text-white/55">
                    — {q.source}{q.date && ` · ${q.date}`}
                  </footer>
                </blockquote>
              ))}
            </div>
          </Section>
        )}

        {/* FOOTER CTA */}
        <div className="pt-8 border-t border-white/8 flex flex-wrap items-center justify-between gap-4">
          <div className="text-[11px] text-white/45 tracking-[0.2em] uppercase">
            Powered by Call·Sheet
          </div>
          <Link to="/register" className="text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-white underline underline-offset-4"
                data-testid="public-tg-cta-register">
            Book this group <ArrowRight size={11} className="inline ml-1"/>
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, testId, children, empty }) {
  return (
    <section data-testid={testId}>
      <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white/95 mb-5">
        {title}
      </h2>
      {children}
      {empty && (
        <div className="cs-card rounded-xl p-10 text-center text-white/50 text-sm">
          {empty}
        </div>
      )}
    </section>
  );
}

function ProductionCard({ p, styleLabel, accent, upcoming }) {
  return (
    <div className="cs-card rounded-xl p-5" data-testid={`public-tg-prod-${p.id}`}>
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
             style={{ background: `${accent}22`, color: accent }}>
          <Drama size={18}/>
        </div>
        {upcoming && (
          <span className="text-[9px] tracking-[0.2em] uppercase text-emerald-300">
            <Clock size={9} className="inline mr-0.5"/> Upcoming
          </span>
        )}
      </div>
      <div className="mt-3">
        <div className="font-display text-base uppercase tracking-tight">{p.title}</div>
        <div className="text-[11px] text-white/60 mt-1">
          {p.playwright && <span>by {p.playwright}</span>}
          {p.language && <span> · {p.language}</span>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {p.style && (
          <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase"
                style={{ background: `${accent}18`, color: accent }}>
            {styleLabel(p.style)}
          </span>
        )}
        {p.opening_date && (
          <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase bg-white/8 text-white/60">
            <Calendar size={9} className="inline mr-1"/> {p.opening_date}
          </span>
        )}
        {p.cast_size > 0 && (
          <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase bg-white/8 text-white/60">
            <Users size={9} className="inline mr-1"/> {p.cast_size}
          </span>
        )}
      </div>
      {p.synopsis && (
        <p className="mt-3 text-[11px] text-white/55 line-clamp-4 leading-relaxed">
          {p.synopsis}
        </p>
      )}
    </div>
  );
}
